'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowRight, Bell, Check, ChevronDown, Globe, X } from 'lucide-react';
import { getCountries, Country } from 'react-phone-number-input';
import { User } from 'hotdesk_typescript';
import flags from 'react-phone-number-input/flags';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { usePhoneLabels } from '@/hotdesk-ui/lib/phone-labels';
import { useCompanySetupCountries } from '@/hotdesk-ui/hooks/useCompanySetupCountries';
import CompanySetupModal from './CompanySetupModal';
import styles from './CompanySetupCountryPopup.module.scss';
interface CompanySetupCountryPopupProps {
  show: boolean;
  onClose: () => void;
}

/**
 * Entry pop-up for the company setup flow ("For Businesses" → "Company
 * Setup"). Shows the supported countries as primary buttons plus a dropdown
 * with every other country; picking an unsupported one swaps to the
 * "not available yet — sign up to get notified" view (same idea as the
 * no-workspaces expansion request).
 */
const CompanySetupCountryPopup: FC<CompanySetupCountryPopupProps> = ({
  show,
  onClose
}) => {
  const t = useTranslations('companySetup.countryPopup');
  const tPhone = useTranslations('phone-input');
  const router = useRouter();
  const locale = useLocale();
  const labels = usePhoneLabels();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const countries = useCompanySetupCountries();
  const supportedCountries = useMemo(() => countries.filter(country => country.supported), [countries]);
  const [view, setView] = useState<'select' | 'unsupported'>('select');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [notifyPending, setNotifyPending] = useState(false);
  const [notifyConfirmed, setNotifyConfirmed] = useState(false);
  const [requestedCountry, setRequestedCountry] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const supportedCodes = useMemo(() => new Set(supportedCountries.map(country => country.code)), [supportedCountries]);
  const otherCountries = useMemo(() => getCountries().filter(code => !supportedCodes.has(code)).map(code => ({
    code,
    name: labels[code] as string || code
  })).sort((a, b) => a.name.localeCompare(b.name)), [labels, supportedCodes]);
  const filteredCountries = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return otherCountries;
    return otherCountries.filter(country => country.name.toLowerCase().includes(query));
  }, [otherCountries, countrySearch]);

  // Reset to the country list every time the popup opens.
  useEffect(() => {
    if (show) {
      setView('select');
      setDropdownOpen(false);
      setCountrySearch('');
      setNotifyPending(false);
      setNotifyConfirmed(false);
      setRequestedCountry(null);
    }
  }, [show]);

  /**
   * Records the request in the forms spreadsheet ("CS - REQ Country").
   * Shows the confirmation straight away and takes it back if the
   * request fails, so the user can try again.
   */
  const requestCountry = async (requester: User) => {
    if (!requestedCountry) return;
    setNotifyConfirmed(true);
    try {
      const res = await fetch('/api/company-setup-country-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          countryCode: requestedCountry,
          fullName: `${requester.firstName || ''} ${requester.lastName || ''}`.trim(),
          email: requester.email,
          phone: requester.phoneNumber || '',
          locale
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Company setup country request failed', res.status, err?.error);
        setNotifyConfirmed(false);
      }
    } catch (err) {
      console.error('Company setup country request failed', err);
      setNotifyConfirmed(false);
    }
  };

  // If sign-in was triggered from the notify CTA, send once authed.
  useEffect(() => {
    if (notifyPending && user) {
      setNotifyPending(false);
      void requestCountry(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyPending, user]);
  useEffect(() => {
    if (!dropdownOpen) return;
    const onClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [dropdownOpen]);
  const handleSupportedCountry = (slug: string) => {
    onClose();
    router.push(`/company-setup/${slug}`);
  };
  const handleOtherCountry = (code: string) => {
    setRequestedCountry(code);
    setDropdownOpen(false);
    setView('unsupported');
  };
  const handleNotifyClick = () => {
    if (notifyConfirmed) return;
    if (!user) {
      setNotifyPending(true);
      new AuthPopupService().getPopup();
      return;
    }
    void requestCountry(user);
  };
  return <CompanySetupModal show={show} onClose={onClose} dialogClassName={styles.dialog} data-sentry-element="CompanySetupModal" data-sentry-component="CompanySetupCountryPopup" data-sentry-source-file="CompanySetupCountryPopup.tsx">
            <button type="button" className={styles.close} aria-label={t('close')} onClick={onClose}>
                <X size={20} data-sentry-element="X" data-sentry-source-file="CompanySetupCountryPopup.tsx" />
            </button>

            {view === 'select' ? <div className={styles.selectView}>
                    <h2 className={styles.title}>{t('title')}</h2>
                    <p className={styles.subtitle}>{t('subtitle')}</p>
                    <p className={styles.description}>{t('description')}</p>

                    {supportedCountries.map(country => {
        // SVG flags render consistently; Windows shows
        // emoji flags as plain letters.
        const Flag = flags[country.code as Country];
        const countryName = labels[country.code as Country] as string || country.code;
        return <button key={country.code} type="button" className={styles.supportedButton} onClick={() => handleSupportedCountry(country.slug)}>
                                <span className={styles.supportedFlag}>
                                    {Flag ? <Flag title={countryName} /> : null}
                                </span>
                                {countryName}
                                <ArrowRight size={20} className={styles.supportedArrow} />
                            </button>;
      })}

                    <p className={styles.cannotSee}>{t('cannotSee')}</p>

                    <div className={styles.dropdownWrapper} ref={dropdownRef}>
                        <button type="button" className={styles.dropdownTrigger} aria-expanded={dropdownOpen} onClick={() => setDropdownOpen(open => !open)}>
                            <Globe size={16} />
                            <span className={styles.dropdownPlaceholder}>
                                {t('selectCountry')}
                            </span>
                            <ChevronDown size={16} className={`${styles.dropdownChevron} ${dropdownOpen ? styles.dropdownChevronOpen : ''}`} />
                        </button>

                        {dropdownOpen && <div className={styles.dropdownMenu}>
                                <div className={styles.dropdownSearch}>
                                    <input type="text" autoFocus value={countrySearch} placeholder={tPhone('search-placeholder')} onChange={event => setCountrySearch(event.target.value)} />
                                </div>
                                {filteredCountries.map(country => {
            const Flag = flags[country.code as Country];
            return <button key={country.code} type="button" className={styles.dropdownItem} onClick={() => handleOtherCountry(country.code)}>
                                            <span className={styles.itemFlag}>
                                                {Flag ? <Flag title={country.name} /> : null}
                                            </span>
                                            {country.name}
                                        </button>;
          })}
                                {filteredCountries.length === 0 && <p className={styles.dropdownEmpty}>
                                        {tPhone('no-results')}
                                    </p>}
                            </div>}
                    </div>
                </div> : <div className={styles.unsupportedView}>
                    <Image src="/assets/icons/morphy/sad-morphy.svg" alt="" width={96} height={96} className={styles.sadMorphy} />
                    <p className={styles.unsupportedTitle}>
                        {t('unsupportedTitle')}
                    </p>
                    <p className={styles.unsupportedSubtitle}>
                        {t('unsupportedSubtitleLine1')}
                        <br />
                        {t('unsupportedSubtitleLine2')}
                    </p>
                    <button type="button" className={`${styles.notifyButton} ${notifyConfirmed ? styles.notifyConfirmed : ''}`} onClick={handleNotifyClick} disabled={notifyConfirmed}>
                        {notifyConfirmed ? <>
                                <Check size={16} />
                                {t('notifyConfirmed')}
                            </> : <>
                                <Bell size={16} />
                                {user ? t('notifyMe') : t('loginToNotify')}
                            </>}
                    </button>
                </div>}
        </CompanySetupModal>;
};
export default CompanySetupCountryPopup;